import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, X, Eye, Send, Plus, CheckSquare, Square } from 'lucide-react'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
// Checkbox will use native input instead
import { useToast } from 'contexts/ToastContext'
import { hiringProcessApi, StepAction, Candidate, StepProgress, HiringStep, emailConfigApi } from 'lib/api'
import { sanitizeTextWithBreaks } from 'lib/sanitize'
import { useEmailSending } from 'contexts/EmailSendingContext'

interface BulkSendEmailModalProps {
  isOpen: boolean
  onClose: () => void
  stepId: number
  stepName: string
  stepType: string
  candidates: Candidate[]
  stepActions: StepAction[]
  offerId?: number
  hiringSteps?: HiringStep[]
}

export function BulkSendEmailModal({
  isOpen,
  onClose,
  stepId,
  stepName,
  stepType,
  candidates,
  stepActions,
  offerId,
  hiringSteps = []
}: BulkSendEmailModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { startEmailTask } = useEmailSending()
  
  const [selectedCandidates, setSelectedCandidates] = useState<Set<number>>(new Set())
  const [selectedAction, setSelectedAction] = useState<StepAction | null>(null)
  const [preview, setPreview] = useState<{ subject: string; body: string; from_email: string; from_name: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0, currentCandidate: '' })

  // Set default action when stepActions change
  useEffect(() => {
    if (stepActions.length > 0 && !selectedAction) {
      setSelectedAction(stepActions[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepActions])

  // Select all candidates by default when modal opens
  useEffect(() => {
    if (isOpen && candidates.length > 0) {
      setSelectedCandidates(new Set(candidates.map(c => c.id)))
    } else if (!isOpen) {
      setSelectedCandidates(new Set())
      setPreview(null)
      setShowPreview(false)
      setSelectedAction(null)
    }
  }, [isOpen, candidates])

  // Prepare email content from action configuration
  const prepareEmailContent = (action: StepAction, candidate: Candidate) => {
    const config = action.configuration || {}
    const template_id = config.template_id
    const subject = config.subject || ''
    const body = config.body || ''
    
    // Get email config for context
    const context = {
      candidate_name: candidate.name || candidate.email.split('@')[0],
      candidate_email: candidate.email,
      step_name: stepName,
      company_name: 'Your Company', // Could be fetched from settings
    }
    
    return {
      template_id: template_id || undefined,
      subject: subject,
      body: body,
      context
    }
  }

  const toggleCandidate = (candidateId: number) => {
    setSelectedCandidates(prev => {
      const newSet = new Set(prev)
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId)
      } else {
        newSet.add(candidateId)
      }
      return newSet
    })
  }

  const toggleAll = () => {
    if (selectedCandidates.size === candidates.length) {
      setSelectedCandidates(new Set())
    } else {
      setSelectedCandidates(new Set(candidates.map(c => c.id)))
    }
  }

  const handlePreview = async () => {
    if (selectedCandidates.size === 0) {
      addToast({ type: 'error', title: t('common.error'), message: 'Please select at least one candidate' })
      return
    }

    if (!selectedAction) {
      addToast({ type: 'error', title: t('common.error'), message: 'Please select an email action' })
      return
    }

    try {
      setLoading(true)
      
      // Get first candidate for preview
      const firstCandidateId = Array.from(selectedCandidates)[0]
      const firstCandidate = candidates.find(c => c.id === firstCandidateId)
      
      if (!firstCandidate) {
        addToast({ type: 'error', title: t('common.error'), message: 'Candidate not found' })
        return
      }

      // Prepare email content from action
      // Prepare email content from action
      const emailContent = prepareEmailContent(selectedAction, firstCandidate)
      
      // Get email configuration for from_email and from_name
      const emailConfigResponse = await emailConfigApi.getConfig()
      const emailConfigs = Array.isArray(emailConfigResponse.data) 
        ? emailConfigResponse.data 
        : (emailConfigResponse.data as any)?.results || []
      const activeConfig = emailConfigs.find((c: any) => c.is_active) || emailConfigs[0]
      
      // Prepare preview data
      let previewSubject = emailContent.subject
      let previewBody = emailContent.body
      
      // If using template, load it
      if (emailContent.template_id) {
        try {
          const templateResponse = await emailConfigApi.getTemplate(emailContent.template_id)
          const template = templateResponse.data
          previewSubject = template.subject
          previewBody = template.body
        } catch (error) {
          addToast({ type: 'error', title: t('common.error'), message: t('candidates.bulkEmail.failedToLoadTemplate') })
          return
        }
      }
      
      // Replace template variables (support both {variable} and {{variable}} formats)
      for (const [key, value] of Object.entries(emailContent.context)) {
        const regex1 = new RegExp(`\\{${key}\\}`, 'g')
        const regex2 = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
        previewSubject = previewSubject.replace(regex1, String(value)).replace(regex2, String(value))
        previewBody = previewBody.replace(regex1, String(value)).replace(regex2, String(value))
      }
      
      // Get all selected candidates' emails
      const selectedEmails = candidates
        .filter(c => selectedCandidates.has(c.id))
        .map(c => c.email)
      
      setPreview({
        subject: previewSubject,
        body: previewBody,
        recipients: selectedEmails,
        from_email: activeConfig?.from_email || 'noreply@example.com',
        from_name: activeConfig?.from_name || 'ATS System'
      })
      setShowPreview(true)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || t('candidates.bulkEmail.failedToPreview')
      addToast({
        type: 'error',
        title: t('common.error'),
        message: errorMessage
      })
      console.error('Preview email error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if (selectedCandidates.size === 0) {
      addToast({ type: 'error', title: t('common.error'), message: t('candidates.bulkEmail.pleaseSelectCandidates') })
      return
    }

    if (!selectedAction) {
      addToast({ type: 'error', title: t('common.error'), message: t('candidates.bulkEmail.pleaseSelectAction') })
      return
    }

    try {
      setSending(true)
      const candidateIds = Array.from(selectedCandidates)
      const total = candidateIds.length
      
      // Always use Celery task for async sending to avoid rate limiting issues
      // This allows proper rate limiting management in the background
      // Mailtrap has strict rate limits, so async is the best approach
      try {
        const emailContent = prepareEmailContent(selectedAction, candidates[0])
        
        // Don't replace variables here - backend will replace them per candidate
        // Just send the template subject/body with placeholders
        // Use bulk email API with Celery (backend will handle variable replacement)
        const response = await emailConfigApi.sendBulkCandidateEmails({
          candidate_ids: candidateIds.map(id => Number(id)), // Ensure all IDs are numbers
          template_id: emailContent.template_id,
          subject: emailContent.subject || undefined,
          body: emailContent.body || undefined,
          action_name: selectedAction.name || 'Bulk Email',
          delay_between_emails: 10 
        })
        
          // Register task in context for tracking
          startEmailTask(response.data.task_id, total)
          
          addToast({
            type: 'success',
            title: t('candidates.bulkEmail.bulkEmailStarted'),
            message: t('candidates.bulkEmail.bulkEmailStartedMessage', { total })
          }, 5000)
          
          onClose()
          return
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || error.message || t('candidates.bulkEmail.failedToStart')
          addToast({
            type: 'error',
            title: t('common.error'),
            message: errorMessage
          })
          setSending(false)
          return
        }
      } catch (error: any) {
        addToast({
          type: 'error',
          title: t('common.error'),
          message: error.response?.data?.error || t('candidates.bulkEmail.failedToSend')
        })
    } finally {
      setSending(false)
      setSendingProgress({ current: 0, total: 0, currentCandidate: '' })
    }
  }

  const selectedCandidatesList = candidates.filter(c => selectedCandidates.has(c.id))

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('candidates.bulkEmail.title', { stepName })}
      size="lg"
    >
      <div className="space-y-6">
        {/* Email Action Selection */}
        {stepActions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('candidates.bulkEmail.selectEmailAction')} *
            </label>
            <select
              value={selectedAction?.id || ''}
              onChange={(e) => {
                const action = stepActions.find(a => a.id === Number(e.target.value))
                setSelectedAction(action || null)
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">{t('candidates.bulkEmail.selectEmailActionPlaceholder')}</option>
              {stepActions.map(action => (
                <option key={action.id} value={action.id}>
                  {action.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Candidate Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('candidates.bulkEmail.selectCandidatesCount', { selected: selectedCandidates.size, total: candidates.length })}
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleAll}
              className="text-xs"
            >
              {selectedCandidates.size === candidates.length ? (
                <>
                  <CheckSquare className="h-4 w-4 mr-1" />
                  {t('candidates.bulkEmail.deselectAll')}
                </>
              ) : (
                <>
                  <Square className="h-4 w-4 mr-1" />
                  {t('candidates.bulkEmail.selectAll')}
                </>
              )}
            </Button>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
            {candidates.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                {t('candidates.bulkEmail.noCandidates')}
              </p>
            ) : (
              candidates.map((candidate) => {
                const isSelected = selectedCandidates.has(candidate.id)
                return (
                  <div
                    key={candidate.id}
                    className="flex items-center gap-3 p-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleCandidate(candidate.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {candidate.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {candidate.email}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
          
        </div>

        {/* Preview Section */}
        {showPreview && preview && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{t('candidates.bulkEmail.emailPreview')}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('candidates.bulkEmail.from')}</label>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {preview.from_name} &lt;{preview.from_email}&gt;
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('candidates.bulkEmail.to')}</label>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {selectedCandidatesList.map(c => c.email).join(', ')}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('candidates.bulkEmail.subject')}</label>
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                {preview.subject}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('candidates.bulkEmail.body')}</label>
              <div 
                className="text-sm text-gray-700 dark:text-gray-300 mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded border"
                dangerouslySetInnerHTML={{ __html: sanitizeTextWithBreaks(preview.body).replace(/\n/g, '<br>') }}
              />
            </div>
          </div>
        )}

        {/* Sending Progress */}
        {sending && sendingProgress.total > 0 && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                {t('candidates.bulkEmail.sendingEmails', { current: sendingProgress.current, total: sendingProgress.total })}
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400">
                {Math.round((sendingProgress.current / sendingProgress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(sendingProgress.current / sendingProgress.total) * 100}%` }}
              />
            </div>
            {sendingProgress.currentCandidate && (
              <p className="text-xs text-blue-700 dark:text-blue-400">
                {t('candidates.bulkEmail.current', { candidate: sendingProgress.currentCandidate })}
              </p>
            )}
            <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
              {t('candidates.bulkEmail.note')}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={sending}
          >
            {t('candidates.bulkEmail.cancel')}
          </Button>
          {!showPreview && (
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={loading || sending || selectedCandidates.size === 0 || !selectedAction}
            >
              <Eye className="h-4 w-4 mr-2" />
              {t('candidates.bulkEmail.preview')}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSend}
            disabled={loading || sending || selectedCandidates.size === 0 || !selectedAction}
          >
            <Send className="h-4 w-4 mr-2" />
            {sending 
              ? t('candidates.bulkEmail.sendingProgress', { current: sendingProgress.current, total: sendingProgress.total })
              : t('candidates.bulkEmail.sendToCandidates', { count: selectedCandidates.size })}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

